// authSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getRolePermissions } from "../../services/auth.services";
import { getCookie, removeCookie } from "../../utils/cookies/cookieUtils";
import { AuthState, User } from "../../types/authType";
import { RolePermissionsType } from "../../types/common";

const parseCookie = (cookieName: string, fallback: string) =>
  JSON.parse(getCookie(cookieName) || fallback);

const user = parseCookie("user", "{}");
const token = getCookie("token") || null;
const refreshToken = getCookie("refreshToken") || null;

const initialState: AuthState = {
  isAuthenticated: !!token,
  token: token,
  refreshToken: refreshToken,
  user: Object.keys(user).length > 0 ? user : null,
  loading: true,
  error: null,
  rolePermissions:{},
};

export const fetchRolePermissions = createAsyncThunk(
  "auth/fetchRolePermissions",
  async (role_id: string, { rejectWithValue }) => {
    try {
      const res = await getRolePermissions({ role_id });

      if (res.status !== 200) {
        throw new Error("Failed to fetch role permissions");
      }

      const data = await res.data; // Parse the response
      return data.data; // Return the permissions
    } catch (error: any) {
      return rejectWithValue(error.message); // Handle errors
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (
      state,
      action: PayloadAction<{ token: string; refreshToken: string; user: User }>
    ) => {
      state.isAuthenticated = true;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.user = action.payload.user;
      state.loading = false;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.token = null;
      state.refreshToken = null;
      state.user = null;
      state.rolePermissions = {};
      
      // Clear the cookies
      removeCookie("token");
      removeCookie("refreshToken");
      removeCookie("user");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRolePermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchRolePermissions.fulfilled,
        (state, action: PayloadAction<RolePermissionsType>) => {
          state.rolePermissions = action.payload;
          state.loading = false;
        }
      )
      .addCase(fetchRolePermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { loginRequest, loginSuccess, loginFailure, logout } =
  authSlice.actions;
export default authSlice.reducer;
